import usePrivateMedia from '../../hooks/usePrivateMedia';
import React from 'react';
import { FileText, ExternalLink, Volume2 } from 'lucide-react';

const MAX_IMAGE_SIZE = 300;
const DEFAULT_ATTACHMENT_NAMES = {
  image: 'Image',
  pdf: 'PDF Document',
  audio: 'Audio Recording',
  video: 'Video Recording',
};
const DEFAULT_MIME_TYPES = {
  pdf: 'application/pdf',
  audio: 'audio/mpeg',
  video: 'video/mp4',
};

const MessageAttachment = ({ message, onImageClick }) => {
  if (message.role !== 'user') return null;

  const normalizedAttachments = Array.isArray(message.attachments)
    ? message.attachments
      .filter((attachment) =>
        attachment && (attachment.url || attachment.previewUrl)
      )
      .map((attachment, index) => ({
        ...attachment,
        id: attachment.id || `${message.id}-attachment-${index}`,
        type: attachment.type || 'file',
        name: attachment.name || DEFAULT_ATTACHMENT_NAMES[attachment.type] || 'Attachment',
      }))
    : [];

  if (normalizedAttachments.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 mb-2 self-end items-end">
      {normalizedAttachments.map((attachment) => (
        <Attachment key={attachment.id} attachment={attachment} onImageClick={onImageClick} />
      ))}
    </div>
  );
};

const Attachment = ({ attachment, onImageClick }) => {
  const displayUrl = usePrivateMedia(attachment.url || attachment.previewUrl);
  const attachmentName = attachment.name || 'Attachment';
  const isUploading = attachment.isLocal || attachment.uploading;
  if (!displayUrl) return <span role="status">{displayUrl === null ? 'Unable to access attachment.' : 'Loading attachment…'}</span>;

  if (attachment.type === 'image' && displayUrl) {
    return (
      <div key={attachment.id} className="relative max-w-full overflow-hidden">
        <img
          src={displayUrl}
          alt={attachmentName}
          className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          style={{
            maxWidth: `${MAX_IMAGE_SIZE}px`,
            maxHeight: `${MAX_IMAGE_SIZE}px`,
            objectFit: 'contain',
          }}
          onClick={(e) => onImageClick && onImageClick(displayUrl, attachmentName, e.target)}
          title="Click to expand"
        />
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white text-sm rounded-lg">
            Uploading...
          </div>
        )}
      </div>
    );
  }

  if (attachment.type === 'pdf' && displayUrl) {
    return (
      <div
        key={attachment.id}
        className="flex flex-col gap-2 p-3 theme-chat-bubble-user rounded-3xl max-w-md"
      >
        <div className="flex items-center gap-2">
          <FileText className="theme-chat-bubble-user-text" size={24} />
          <div className="flex flex-col flex-grow">
            <span className="text-sm theme-chat-bubble-user-text">
              {attachmentName}
            </span>
            <a
              href={displayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm flex items-center gap-1"
            >
              Open PDF <ExternalLink size={14} />
            </a>
          </div>
        </div>

        <div className="w-full h-[400px] mt-2 rounded-xl overflow-hidden">
          <embed
            src={`${displayUrl}#view=FitH`}
            type="application/pdf"
            width="100%"
            height="100%"
          />
        </div>
      </div>
    );
  }

  if (attachment.type === 'audio' && displayUrl) {
    return (
      <div
        key={attachment.id}
        className="flex flex-col gap-2 p-3 theme-chat-bubble-user rounded-3xl max-w-md"
      >
        <div className="flex items-center gap-2">
          <Volume2 className="theme-chat-bubble-user-text" size={24} />
          <div className="flex flex-col flex-grow">
            <span className="text-sm theme-chat-bubble-user-text">
              {attachmentName}
            </span>
            <a
              href={displayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm flex items-center gap-1"
            >
              Download <ExternalLink size={14} />
            </a>
          </div>
        </div>

        <audio controls className="w-full mt-2" preload="metadata">
          <source src={displayUrl} type={attachment.mimeType || DEFAULT_MIME_TYPES.audio} />
          Your browser does not support the audio element.
        </audio>
      </div>
    );
  }

  if (attachment.type === 'video' && displayUrl) {
    return (
      <div
        key={attachment.id}
        className="flex flex-col gap-2 p-3 theme-chat-bubble-user rounded-3xl max-w-md"
      >
        <div className="flex items-center gap-2">
          <FileText className="theme-chat-bubble-user-text" size={24} />
          <div className="flex flex-col flex-grow">
            <span className="text-sm theme-chat-bubble-user-text">
              {attachmentName}
            </span>
            <a
              href={displayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm flex items-center gap-1"
            >
              Download <ExternalLink size={14} />
            </a>
          </div>
        </div>

        <div className="w-full mt-2 rounded-xl overflow-hidden">
          <video
            controls
            className="w-full"
            preload="metadata"
            style={{ maxHeight: '400px' }}
          >
            <source src={displayUrl} type={attachment.mimeType || DEFAULT_MIME_TYPES.video} />
            Your browser does not support the video element.
          </video>
        </div>
      </div>
    );
  }

  return <a href={displayUrl} download={attachmentName}>{attachmentName}</a>;
};

export default MessageAttachment;
